// =====================================================================
// External-link typing (product rule 7). The design renders links as
// typed cards — Drive doc / Video / Report / Generic — with the type
// auto-detected from the URL and an icon per type. The backend's
// PlayerFolderLink.category enum is {video, document, scout_report,
// other}; we auto-detect into that enum so the stored value stays
// backend-valid while the UI shows the design's four identities.
//
//   design "Drive doc" -> document
//   design "Video"     -> video
//   design "Report"    -> scout_report
//   design "Generic"   -> other
// =====================================================================

export const LINK_CATEGORY = {
  VIDEO: "video",
  DOCUMENT: "document",
  REPORT: "scout_report",
  OTHER: "other",
};

const VIDEO_HOSTS = [
  "youtube.com", "youtu.be", "vimeo.com", "hudl.com", "veo.co",
  "wyscout.com", "dailymotion.com", "twitch.tv", "streamable.com",
];
const DRIVE_HOSTS = [
  "drive.google.com", "docs.google.com", "dropbox.com", "onedrive.live.com",
  "sharepoint.com", "box.com", "1drv.ms",
];
const VIDEO_EXT = [".mp4", ".mov", ".webm", ".m4v"];
const REPORT_HINTS = ["scout", "report", "analysis", "dossier"];

function safeUrl(url) {
  try {
    return new URL(String(url).trim());
  } catch {
    // Tolerate bare "example.com/x" by prepending a scheme.
    try {
      return new URL("https://" + String(url).trim());
    } catch {
      return null;
    }
  }
}

// Auto-detect the backend category from a URL. Deterministic and pure
// so it can be unit-tested. Order matters: explicit video/drive hosts
// win, then video file extensions, then report hints, else "other".
export function detectLinkCategory(url) {
  const u = safeUrl(url);
  if (!u) return LINK_CATEGORY.OTHER;
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const path = (u.pathname + u.search).toLowerCase();
  const full = (host + path).toLowerCase();

  if (VIDEO_HOSTS.some((h) => host === h || host.endsWith("." + h))) return LINK_CATEGORY.VIDEO;
  if (DRIVE_HOSTS.some((h) => host === h || host.endsWith("." + h))) return LINK_CATEGORY.DOCUMENT;
  if (VIDEO_EXT.some((ext) => path.endsWith(ext))) return LINK_CATEGORY.VIDEO;
  if (path.endsWith(".pdf")) return LINK_CATEGORY.REPORT;
  if (REPORT_HINTS.some((h) => full.includes(h))) return LINK_CATEGORY.REPORT;
  return LINK_CATEGORY.OTHER;
}

// Per-category presentation: label + pill colors matching the design's
// typed link cards. `icon` is a lucide-react component name resolved by
// the card component (kept as a string here so this stays a pure,
// dependency-free constants module that Jest can import).
export const LINK_TYPE_META = {
  [LINK_CATEGORY.VIDEO]: { label: "Video", icon: "Video", bg: "#FEF3C7", color: "#92400E" },
  [LINK_CATEGORY.DOCUMENT]: { label: "Drive doc", icon: "FileText", bg: "#DBEAFE", color: "#1E40AF" },
  [LINK_CATEGORY.REPORT]: { label: "Report", icon: "ClipboardList", bg: "#E0E7FF", color: "#3730A3" },
  [LINK_CATEGORY.OTHER]: { label: "Link", icon: "LinkIcon", bg: "#F3F4F6", color: "#374151" },
};

export function linkTypeMeta(category) {
  return LINK_TYPE_META[category] || LINK_TYPE_META[LINK_CATEGORY.OTHER];
}
